import React from "react";
import { Crown, Hotel, MapPin } from "lucide-react";

const ICONS = [
  { icon: Crown, label: "The same pillow", sub: "Used behind every headboard of Dubai's royal suites" },
  { icon: Hotel, label: "Made for 7-star rooms", sub: "Not the one they advertise — the one they actually use" },
  { icon: MapPin, label: "Now within reach", sub: "Ritz Paris · Burj Al Arab · Hilton Presidential" },
];

export default function WorldsBestPillow() {
  return (
    <section
      data-testid="worlds-best-pillow"
      className="relative overflow-hidden bg-[#0e0e10] text-white py-20 md:py-28"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#d8f06b]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#b8952a]/15 blur-3xl pointer-events-none" />

      <div className="container-x relative">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#d8f06b]">
            <span className="w-6 h-px bg-[#d8f06b]/60" /> If you've been looking
          </div>

          <h2 className="mt-5 text-[36px] sm:text-[52px] md:text-[68px] leading-[0.96] font-black tracking-[-0.025em]">
            You haven't found
            <br />
            this <span className="italic font-serif" style={{ color: "#d8f06b" }}>anywhere else.</span>
            <br />
            And there's a reason.
          </h2>

          <p className="mt-7 text-[16px] md:text-[19px] text-white/75 max-w-2xl leading-relaxed">
            The pillow you're searching for isn't sold in stores. It's hand-stitched in
            Dubai for the rooms where billionaires sleep — the{" "}
            <strong className="text-white">Burj Al Arab royal suites</strong>, the
            {" "}<strong className="text-white">Ritz Paris</strong>, the
            {" "}<strong className="text-white">Hilton presidential floors</strong>.
            The good ones never make it to retail. They're made in tiny batches, by hand,
            for places that pay $1,800 a night and don't ask the price. We make the same
            one. We just decided it shouldn't take a $1,800 hotel bill to lay your head
            on it.
          </p>

          <div className="mt-9 grid sm:grid-cols-3 gap-3 max-w-3xl">
            {ICONS.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  className="rounded-2xl p-4 bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition"
                >
                  <span className="w-9 h-9 rounded-xl inline-flex items-center justify-center bg-[#d8f06b]/15 text-[#d8f06b]">
                    <Icon size={18} />
                  </span>
                  <div className="mt-2.5 text-[13px] font-black text-white leading-tight">
                    {it.label}
                  </div>
                  <div className="mt-1 text-[11px] text-white/55 leading-snug">
                    {it.sub}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Value contrast */}
          <div className="mt-12 flex flex-wrap items-end gap-x-10 gap-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-white/40">
                What a hotel night costs you
              </div>
              <div className="mt-1 text-[32px] md:text-[40px] font-black text-white/50 line-through tabular-nums">
                $1,800<span className="text-[15px] no-underline ml-1">/ night</span>
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">Burj Al Arab — Royal Suite</div>
            </div>
            <div className="text-white/30 text-[24px] font-light">→</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#d8f06b]">
                What you pay, forever
              </div>
              <div className="mt-1 text-[44px] md:text-[56px] font-black text-white tabular-nums leading-none">
                $147
              </div>
              <div className="text-[12px] text-white/60 mt-1">
                One pillow · 100-night trial · ships worldwide
              </div>
            </div>
          </div>

          <a
            href="#shop"
            data-testid="worlds-best-cta"
            className="inline-flex items-center gap-2 mt-10 px-7 py-3.5 rounded-full bg-[#d8f06b] text-[#0e0e10] font-extrabold text-[13px] uppercase tracking-[0.12em] hover:bg-white transition"
          >
            Step into the 1% of sleepers →
          </a>
          <p className="mt-3 text-[11px] text-white/40">
            Luxury, finally at a price you cannot ignore. The bedroom of a sheikh, in the comfort of your home.
          </p>
        </div>
      </div>
    </section>
  );
}
